import React from "react";
import PropTypes from "prop-types";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Collapse from "@mui/material/Collapse";
import SFTypography from "../../../typography";
import { SFStack } from "../../../grid";
import {
  SFArrowRightIcon,
  SFExpandLessIcon,
  SFExpandMoreIcon,
} from "../../../icons";
import getStyled from "../../../../hooks/getStyled";

const { styled } = getStyled();

const MenuItemStyled = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "disableBorder",
})(({ theme, disableBorder = false }) => ({
  width: "100%",
  minHeight: "36px",
  padding: "6px 10px",
  ...(!disableBorder && {
    borderBottom: `1px solid ${theme.palette.grey200.main}`,
  }),
  "&.Mui-selected": {
    backgroundColor: theme.palette.grey100.main,
  },
}));

const SubmenuItem = ({
  label,
  onClick,
  disableBorder,
  selectedItems,
  disabled,
}) => {
  return (
    <MenuItemStyled
      onClick={onClick}
      disableBorder={disableBorder}
      selected={selectedItems.includes(label)}
      disabled={disabled}
    >
      <SFStack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ width: "100%", textWrap: "wrap"}}
      >
        <SFArrowRightIcon size="xsmall" color="grey450" />
        <SFTypography variant="body2">{label}</SFTypography>
      </SFStack>
    </MenuItemStyled>
  );
};

const Submenu = ({
  label,
  onClick,
  actionDefs,
  disableBorder,
  selectedItems,
  closeMenu,
  disabled,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <MenuItemStyled
        onClick={() => {
          onClick();
          setOpen(!open);
        }}
        // Disable border if the submenu is open or if the parent item has a disabled border
        disableBorder={open || disableBorder}
        selected={selectedItems.includes(label)}
        disabled={disabled}
      >
        <SFStack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", textWrap: "wrap" }}
        >
          <SFTypography variant="body2">{label}</SFTypography>
          {open ? (
            <SFExpandLessIcon color="grey350" />
          ) : (
            <SFExpandMoreIcon color="grey350" />
          )}
        </SFStack>
      </MenuItemStyled>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <MenuList
          autoFocusItem={open}
          id="composition-menu"
          aria-labelledby="composition-button"
          disablePadding
        >
          {actionDefs.map((def, index) => (
            <SubmenuItem
              key={index}
              label={def.label}
              onClick={() => {
                def.onClick();
                closeMenu();
              }}
              disableBorder={
                // Disable border for the inbetween items
                index < actionDefs.length - 1 ||
                // Disable border for the last item if the parent item has a disabled border
                (index === actionDefs.length - 1 && disableBorder)
              }
              selectedItems={selectedItems}
              disabled={def.disabled}
            />
          ))}
        </MenuList>
      </Collapse>
    </>
  );
};

const Item = ({ label, onClick, disableBorder, selectedItems, disabled }) => {
  return (
    <MenuItemStyled
      onClick={onClick}
      disableBorder={disableBorder}
      selected={selectedItems.includes(label)}
      disabled={disabled}
    >
      <SFStack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ width: "100%", textWrap: "wrap" }}
      >
        <SFTypography variant="body2">{label}</SFTypography>
      </SFStack>
    </MenuItemStyled>
  );
};

const RegularMenuItem = ({
  actionDef,
  disableBorder,
  closeMenu,
  selectedItems,
}) => {
  const { children = [] } = actionDef;

  if (Array.isArray(children) && children.length === 0) {
    // If there are no children, render a regular item
    return (
      <Item
        label={actionDef.label}
        onClick={() => {
          actionDef.onClick();
          closeMenu();
        }}
        disableBorder={disableBorder}
        selectedItems={selectedItems}
        disabled={actionDef.disabled}
      />
    );
  } else {
    // If there are children, render a submenu
    return (
      <Submenu
        label={actionDef.label}
        onClick={() => {
          actionDef.onClick();
        }}
        actionDefs={children}
        disableBorder={disableBorder}
        selectedItems={selectedItems}
        closeMenu={closeMenu}
        disabled={actionDef.disabled}
      />
    );
  }
};

export default RegularMenuItem;

RegularMenuItem.propTypes = {
  actionDef: PropTypes.shape({
    // The label of the item
    label: PropTypes.string,
    // The onClick function of the item
    onClick: PropTypes.func,
    // The children of the item
    children: PropTypes.array,
  }),
  // Whether to disable the border of the item
  disableBorder: PropTypes.bool,
};
